import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import { getErrorMessage } from '../utils/errorMessage';
import { getLocale } from '../utils/dateLocale';
import { AUTH_API } from '../utils/apiConfig';
import { useNavigate } from 'react-router-dom';
import Menu from '../components/Menu';
import Control from '../components/control';
import { useTheme } from '@mui/material/styles';
import { useNotifications } from '../context/NotificationContext';
import NotificationContainer from '../components/NotificationContainer';
import { 
  TextField, 
  Autocomplete, 
  IconButton, 
  Menu as MuiMenu, 
  MenuItem, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Avatar, 
  Button, 
  Checkbox, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RoomIcon from '@mui/icons-material/Room';
import { normalizeRole, getRoleLabel } from '../utils/roles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const rows = [
  { id: 1, name: 'Иван Иванов', email: 'ivan@example.com', role: 'user', devices: 5, location: 'Москва', avatar: 'https://via.placeholder.com/40' },
  { id: 2, name: 'Петр Петров', email: 'petr@example.com', role: 'admin', devices: 10, location: 'Санкт-Петербург', avatar: 'https://via.placeholder.com/40' },
  { id: 3, name: 'Сергей Сергеев', email: 'sergey@example.com', role: 'manager', devices: 3, location: 'Новосибирск', avatar: 'https://via.placeholder.com/40' },
];

export default function Users() {
  const { t } = useTranslation('admin');
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSuccess, showWarning, showInfo, showError } = useNotifications();

  const columns = [
    { field: 'avatar', headerName: t('usersColumnUser') },
    { field: 'devices', headerName: t('usersColumnDevices') },
    { field: 'email', headerName: t('usersColumnEmail') },
    { field: 'telegram', headerName: t('usersColumnTelegram') },
    { field: 'role', headerName: t('usersColumnRole') },
    { field: 'tags', headerName: t('usersColumnTags') },
    { field: 'hosting_names', headerName: t('usersColumnHostingNames') },
    { field: 'created_at', headerName: t('usersColumnCreatedAt') },
    { field: 'id', headerName: t('usersColumnId') },
  ];
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    name: '',
    email: '',
    telegram: '',
    info: '',
    password: ''
  });
  const [createUserErrors, setCreateUserErrors] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAssignTagDialog, setOpenAssignTagDialog] = useState(false);
  const [assignTags, setAssignTags] = useState([]);
  const [assignTagInput, setAssignTagInput] = useState('');

  const roles = ['client', 'hoster', 'admin', 'super_admin'];

  // Функция для валидации email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Функция для валидации формы создания пользователя
  const validateCreateUserForm = () => {
    const newErrors = {};
    
    if (!createUserData.name.trim()) {
      newErrors.name = t('usersValidationNameRequired');
    }
    
    if (!createUserData.email.trim()) {
      newErrors.email = t('usersValidationEmailRequired');
    } else if (!validateEmail(createUserData.email)) {
      newErrors.email = t('usersValidationEmailInvalid');
    }
    
    if (!createUserData.password) {
      newErrors.password = t('usersValidationPasswordRequired');
    } else if (createUserData.password.length < 6) {
      newErrors.password = t('usersValidationPasswordMinLength');
    }
    
    setCreateUserErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Функция для создания пользователя
  const handleCreateUser = async () => {
    if (!validateCreateUserForm()) {
      return;
    }

    try {
      const response = await api.post(`${AUTH_API}/v1/auth/register`, {
        email: createUserData.email,
        password: createUserData.password,
        name: createUserData.name,
        telegram: createUserData.telegram || undefined,
        info: createUserData.info || undefined
      });
      
      // Закрываем окно и обновляем список пользователей
      setOpenCreateUser(false);
      setCreateUserData({
        name: '',
        email: '',
        telegram: '',
        info: '',
        password: ''
      });
      setCreateUserErrors({});
      
      // Обновляем список пользователей
      await fetchUsers();
      
      // Показываем уведомление об успехе
      setError(null);
    } catch (error) {
      setError(t('usersErrorCreate') + getErrorMessage(error));
    }
  };

  // Функция для обработки изменений в форме
  const handleCreateUserInputChange = (field, value) => {
    setCreateUserData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (createUserErrors[field]) {
      setCreateUserErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Функция для загрузки пользователей
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`${API_BASE_URL}users`);

      if (response.data.users) {
        setUsers(response.data.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      setError(t('usersErrorLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Загружаем данные при монтировании компонента
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleUserClick = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    
    // Поиск по основным полям
    const matchesName = user.name?.toLowerCase().includes(searchLower);
    const matchesTelegram = user.telegram?.toLowerCase().includes(searchLower);
    const matchesEmail = user.email?.toLowerCase().includes(searchLower);
    const matchesId = user.id?.toLowerCase().includes(searchLower);
    
    // Поиск по тегам
    const matchesTags = user.tags ? (
      Array.isArray(user.tags) 
        ? user.tags.some(tag => tag.toLowerCase().includes(searchLower))
        : user.tags.toLowerCase().includes(searchLower)
    ) : false;
    
    const matchesSearch = matchesName || matchesTelegram || matchesEmail || matchesId || matchesTags;
    const matchesRole = selectedRoles.length === 0 || selectedRoles.includes(normalizeRole(user.role));

    return matchesSearch && matchesRole;
  });

  const getRoleStyle = (role) => {
    const base = {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: 1.5,
    };
    const isDark = theme.palette.mode === 'dark';
    switch (normalizeRole(role)) {
      case 'super_admin':
        return { ...base, backgroundColor: '#7b1fa2', color: '#fff' }; // фиолетовый
      case 'admin':
        return { ...base, backgroundColor: '#e5484d', color: '#fff' }; // красный (мягче)
      case 'hoster':
        return { ...base, backgroundColor: '#f5c518', color: '#1a1a1a' }; // жёлтый
      default:
        return {
          ...base,
          backgroundColor: isDark ? 'rgba(141,114,255,0.22)' : 'rgba(37,17,168,0.10)',
          color: isDark ? '#b9a9ff' : '#2511A8',
        }; // клиент — бренд-тон
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(getLocale(), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedRows(filteredUsers.map((_, index) => index));
      showInfo(t('usersSelectedCount', { count: filteredUsers.length }));
    } else {
      setSelectedRows([]);
      showInfo(t('usersSelectionReset'));
    }
  };

  const handleRowSelect = (index) => {
    setSelectedRows((prevSelectedRows) => {
      const exists = prevSelectedRows.includes(index);
      const next = exists
        ? prevSelectedRows.filter((rowIndex) => rowIndex !== index)
        : [...prevSelectedRows, index];
      showInfo(exists ? t('usersRemovedFromSelection') : t('usersAddedToSelection'));
      setSelectAll(next.length === filteredUsers.length && filteredUsers.length > 0);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;
    setOpenDeleteDialog(true);
  };

  const handleAssignHostSelected = () => {
    if (selectedRows.length === 0) return;
    showInfo(t('usersAssignHostInfo', { count: selectedRows.length }));
  };

  const handleAssignTagSelected = () => {
    if (selectedRows.length === 0) return;
    const initial = Array.from(new Set(selectedRows.flatMap((idx) => {
      const u = filteredUsers[idx];
      if (!u || u.tags == null) return [];
      if (Array.isArray(u.tags)) return u.tags.filter(Boolean).map((t) => String(t).trim());
      return String(u.tags).split(',').map((t) => t.trim()).filter(Boolean);
    })));
    setAssignTags(initial);
    setAssignTagInput('');
    setOpenAssignTagDialog(true);
  };

  const handleConfirmDelete = () => {
    const ids = selectedRows
      .map((idx) => filteredUsers[idx]?.id)
      .filter(Boolean);

    setOpenDeleteDialog(false);

    if (ids.length === 0) {
      showWarning(t('usersNoSelectedForDelete'));
      return;
    }

    showInfo(t('usersDeletingUsers'));

    api.post(`${API_BASE_URL}delete_user`, {
      user_ids: ids
    })
    .then((response) => {
      const successMsg =
        (typeof response?.data === 'string' && response.data) ||
        response?.data?.message ||
        t('usersDeletedCount', { count: ids.length });
      showSuccess(successMsg);
      setSelectedRows([]);
      setSelectAll(false);
      fetchUsers();
    })
    .catch((error) => {
      showError(getErrorMessage(error, t('usersUnknownError')));
    });
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    showInfo(t('usersDeleteCancelled'));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = assignTagInput.trim();
      if (!value) return;
      if (!assignTags.includes(value)) {
        setAssignTags((prev) => [...prev, value]);
      }
      setAssignTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setAssignTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleConfirmAssignTag = async () => {
    setOpenAssignTagDialog(false);
    const ids = selectedRows
      .map((idx) => filteredUsers[idx]?.id)
      .filter(Boolean);
    const tagList = assignTags;

    if (ids.length === 0) {
      showWarning(t('usersNoSelectedForTags'));
      return;
    }
    const isDelete = tagList.length === 0;
    showInfo(isDelete ? t('usersTagsDeleteInProgress') : t('usersTagsAssignInProgress'));

    const requests = ids.map((userId) =>
      api.post(`${API_BASE_URL}profile/edit/${userId}`,
        { tags: tagList }
      )
    );

    try {
      const results = await Promise.allSettled(requests);
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failures = results.filter(r => r.status === 'rejected');

      if (successCount > 0) {
        showSuccess(isDelete
          ? t('usersTagsDeleted', { count: successCount })
          : t('usersTagsAssigned', { tags: tagList.join(', '), count: successCount })
        );
      }
      if (failures.length > 0) {
        const first = failures[0].reason?.response?.data;
        const errMsg =
          (typeof first === 'string' && first) ||
          first?.message ||
          first?.error ||
          first?.detail ||
          (Array.isArray(first?.errors) && first.errors.join(', ')) ||
          (first ? JSON.stringify(first) : t('usersTagsAssignErrorFallback'));
        showError(t('usersTagsAssignError', { count: failures.length, error: errMsg }));
      }

      setAssignTags([]);
      setAssignTagInput('');
      fetchUsers();
    } catch (e) {
      showError(t('usersTagsAssignUnknownError'));
    }
  };

  const handleCancelAssignTag = () => {
    setOpenAssignTagDialog(false);
    setAssignTags([]);
    setAssignTagInput('');
    showInfo(t('usersTagsAssignCancelled'));
  };

  // Если загрузка, показываем индикатор
  if (loading) {
    return (
      <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
        <Menu />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>{t('usersLoadingUsers')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Menu />
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <TextField
              label={t('usersSearchLabel')}
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
            />
            <Autocomplete
              multiple
              options={roles}
              value={selectedRoles}
              onChange={(event, newValue) => setSelectedRoles(newValue)}
              getOptionLabel={(option) => getRoleLabel(option, t)}
              renderInput={(params) => <TextField {...params} label={t('usersRoleLabel')} variant="outlined" size="small" />}
            />
            <IconButton onClick={handleFilterClick}>
              <FilterListIcon />
            </IconButton>
            <MuiMenu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
            >
              <MenuItem onClick={handleFilterClose}>{t('usersFilter1')}</MenuItem>
              <MenuItem onClick={handleFilterClose}>{t('usersFilter2')}</MenuItem>
            </MuiMenu>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button variant="contained" color="primary" onClick={() => setOpenCreateUser(true)}>{t('usersCreateUser')}</Button>
            <IconButton>
              <FilterListIcon />
            </IconButton>
          </div>
        </div>

        {/* Показываем ошибку если есть */}
        {error && (
          <Alert severity="error" style={{ marginBottom: '20px' }}>
            {error}
          </Alert>
        )}

        {/* Показываем предупреждение если данных нет */}
        {!loading && !error && users.length === 0 && (
          <Alert severity="warning" style={{ marginBottom: '20px' }}>
            {t('usersNotFound')}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox 
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                {columns.map((col) => (
                  <TableCell key={col.field}>{col.headerName}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user, index) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Checkbox 
                      checked={selectedRows.includes(index)}
                      onChange={() => handleRowSelect(index)}
                    />
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Avatar alt={user.name}>
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </Avatar>
                      <span 
                        style={{ 
                          cursor: 'pointer', 
                          textDecoration: 'underline',
                          color: theme.palette.primary.main
                        }}
                        onClick={() => handleUserClick(user.id)}
                      >
                        {user.name || t('usersNoName')}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{user.device_count || 0}</TableCell>
                  <TableCell>{user.email || 'N/A'}</TableCell>
                  <TableCell>
                    {user.telegram ? (
                      <span
                        style={{
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(`https://t.me/${user.telegram.replace('@', '')}`, '_blank')}
                      >
                        {user.telegram.startsWith('@') ? user.telegram : `@${user.telegram}`}
                      </span>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    <span style={getRoleStyle(user.role)}>
                      {user.role ? getRoleLabel(user.role, t) : 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {user.tags ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {Array.isArray(user.tags) ? user.tags.map((tag, index) => (
                          <span
                            key={index}
                            style={{
                              backgroundColor: theme.palette.secondary.light,
                              color: theme.palette.secondary.contrastText,
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                          >
                            {tag}
                          </span>
                        )) : (
                          <span
                            style={{
                              backgroundColor: theme.palette.secondary.light,
                              color: theme.palette.secondary.contrastText,
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                          >
                            {user.tags}
                          </span>
                        )}
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {user.hosting_names ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {Array.isArray(user.hosting_names) ? user.hosting_names.map((hosting, index) => (
                          <span
                            key={index}
                            style={{
                              backgroundColor: theme.palette.info.light,
                              color: theme.palette.info.contrastText,
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <RoomIcon style={{ fontSize: '14px' }} />
                            {hosting}
                          </span>
                        )) : (
                          <span
                            style={{
                              backgroundColor: theme.palette.info.light,
                              color: theme.palette.info.contrastText,
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <RoomIcon style={{ fontSize: '14px' }} />
                            {user.hosting_names}
                          </span>
                        )}
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>{user.id}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div style={{ paddingTop: '10px' }}>
          <Control
            selectedRows={selectedRows}
            onDelete={handleDeleteSelected}
            onAssignTag={handleAssignTagSelected}
          />
        </div>
        
        {/* Popup для создания пользователя */}
        <Dialog 
          open={openCreateUser} 
          onClose={() => setOpenCreateUser(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('usersDialogCreateTitle')}</DialogTitle>
          <DialogContent>
            <TextField
              label={t('usersFieldName')}
              variant="outlined"
              fullWidth
              margin="normal"
              value={createUserData.name}
              onChange={(e) => handleCreateUserInputChange('name', e.target.value)}
              error={!!createUserErrors.name}
              helperText={createUserErrors.name}
            />
            <TextField
              label={t('usersFieldEmail')}
              variant="outlined"
              fullWidth
              margin="normal"
              value={createUserData.email}
              onChange={(e) => handleCreateUserInputChange('email', e.target.value)}
              error={!!createUserErrors.email}
              helperText={createUserErrors.email}
            />
            <TextField
              label={t('usersFieldPassword')}
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={createUserData.password}
              onChange={(e) => handleCreateUserInputChange('password', e.target.value)}
              error={!!createUserErrors.password}
              helperText={createUserErrors.password || t('usersFieldPasswordHelper')}
            />
            <TextField
              label={t('usersFieldTelegram')}
              variant="outlined"
              fullWidth
              margin="normal"
              value={createUserData.telegram}
              onChange={(e) => handleCreateUserInputChange('telegram', e.target.value)}
              placeholder={t('usersFieldTelegramPlaceholder')}
            />
            <TextField
              label={t('usersFieldInfo')}
              variant="outlined"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={createUserData.info}
              onChange={(e) => handleCreateUserInputChange('info', e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateUser(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={handleCreateUser} variant="contained" color="primary">
              {t('usersCreateUser')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог подтверждения удаления */}
        <Dialog 
          open={openDeleteDialog}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('usersDeleteConfirmTitle')}</DialogTitle>
          <DialogContent>
            <p>{t('usersDeleteConfirmMessage')}</p>
            <ul style={{ marginTop: 8 }}>
              {selectedRows.map((idx) => {
                const u = filteredUsers[idx];
                return (
                  <li key={u?.id || idx}>
                    {u?.name || t('usersNoName')} ({u?.email || 'N/A'})
                  </li>
                );
              })}
            </ul>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>{t('usersCancelButton')}</Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error">{t('usersConfirmButton')}</Button>
          </DialogActions>
        </Dialog>

        {/* Диалог назначения тега */}
        <Dialog 
          open={openAssignTagDialog}
          onClose={handleCancelAssignTag}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('usersAssignTagTitle')}</DialogTitle>
          <DialogContent>
            <p>{t('usersAssignTagMessage')}</p>
            <ul style={{ marginTop: 8 }}>
              {selectedRows.map((idx) => {
                const u = filteredUsers[idx];
                return (
                  <li key={u?.id || idx}>
                    {u?.name || t('usersNoName')} ({u?.email || 'N/A'})
                  </li>
                );
              })}
            </ul>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {assignTags.map((tag) => (
                <Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} />
              ))}
            </div>
            <TextField 
              label={t('usersAddTagLabel')}
              variant="outlined"
              fullWidth
              margin="normal"
              value={assignTagInput}
              onChange={(e) => setAssignTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              placeholder={t('usersAddTagPlaceholder')}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelAssignTag}>{t('usersCancelButton')}</Button>
            <Button onClick={handleConfirmAssignTag} variant="contained" color="primary">{t('usersConfirmButton')}</Button>
          </DialogActions>
        </Dialog>
      </div>
      <NotificationContainer />
    </div>
  );
}
